<template>
	<div class="symbol-picker d-flex align-items-center gap-2">
		<!-- The style of the current selection, drawn the way the pickers and the
		     legend draw it: fill as the background, outline as the border. -->
		<span
			class="symbol-picker-dot"
			:class="{ 'symbol-picker-dot--none': !currentSymbol }"
			:style="currentSymbol ? styleCss(currentSymbol) : undefined"
			aria-hidden="true"
		></span>

		<select
			class="form-select form-select-sm symbol-picker-select"
			:value="modelValue === null ? '' : String(modelValue)"
			@change="onChange"
		>
			<option value="">{{ nullLabel }}</option>
			<option
				v-for="option in symbols"
				:key="option.id"
				:value="String(option.id)"
			>
				{{ option.name }} —
				{{ option.owner === "district" ? "district" : "private" }}
			</option>
			<!-- A bound symbol another part of the library cannot show (it was
			     retired out of this list, or it is someone's private row) still
			     reads as itself instead of silently collapsing to "not set". -->
			<option v-if="unlisted" :value="String(modelValue)">
				Symbol #{{ modelValue }} (not in this library)
			</option>
		</select>

		<span
			v-if="currentSymbol"
			class="badge"
			:class="
				currentSymbol.owner === 'district'
					? 'text-bg-primary'
					: 'text-bg-secondary'
			"
		>
			{{ currentSymbol.owner === "district" ? "district" : "private" }}
		</span>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { styleCss } from "@/lib/symbology";
import type { Symbol } from "@/type/api";

interface Props {
	modelValue: number | null;
	symbols: Symbol[];
	labelForNull?: string;
}
const props = defineProps<Props>();
const emit = defineEmits<{ "update:modelValue": [value: number | null] }>();

const nullLabel = computed(
	() => props.labelForNull ?? "The product default (no symbol)",
);

const currentSymbol = computed<Symbol | null>(
	() => props.symbols.find((symbol) => symbol.id === props.modelValue) ?? null,
);

const unlisted = computed(
	() => props.modelValue !== null && currentSymbol.value === null,
);

function onChange(event: Event) {
	const value = (event.target as HTMLSelectElement).value;
	emit("update:modelValue", value === "" ? null : Number(value));
}
</script>

<style scoped>
.symbol-picker-dot {
	width: 16px;
	height: 16px;
	border-radius: 50%;
	flex: 0 0 auto;
}

.symbol-picker-dot--none {
	background: transparent;
	border: 1px dashed var(--bs-secondary-color);
}

.symbol-picker-select {
	max-width: 22rem;
}
</style>
